/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';

import { StudioDeleteDialogProps } from '../../../types/studioForms';

export const StudioDeleteDialog: React.FC<StudioDeleteDialogProps> = ({
  studio,
  open,
  onClose,
  onConfirm,
  loading = false,
  error = null,
}) => {
  const [step, setStep] = useState<'warning' | 'confirmation' | 'final'>(
    'warning'
  );
  const [nameVerification, setNameVerification] = useState('');
  const [showImpactDetails, setShowImpactDetails] = useState(false);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setStep('warning');
      setNameVerification('');
      setShowImpactDetails(false);
    }
  }, [open]);

  // Check if name verification is correct
  const isNameVerified =
    nameVerification.trim().toLowerCase() === studio.name.toLowerCase();

  // Handle proceeding to confirmation step
  const handleProceedToConfirmation = useCallback(() => {
    setStep('confirmation');
  }, []);

  // Handle proceeding to final step
  const handleProceedToFinal = useCallback(() => {
    if (isNameVerified) {
      setStep('final');
    }
  }, [isNameVerified]);

  // Handle final deletion
  const handleFinalDelete = useCallback(async () => {
    try {
      await onConfirm();
    } catch (deleteError) {
      // Error handling is managed by parent component
      console.error('Delete error:', deleteError);
    }
  }, [onConfirm]);

  // Handle dialog close
  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  // Get dialog title based on step
  const getDialogTitle = () => {
    switch (step) {
      case 'warning':
        return 'Delete Studio?';
      case 'confirmation':
        return 'Confirm Studio Deletion';
      case 'final':
        return 'Final Confirmation';
      default:
        return 'Delete Studio';
    }
  };

  // Render warning step
  const renderWarningStep = () => (
    <>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            This action cannot be undone!
          </Typography>
          <Typography variant="body2">
            Deleting &quot;{studio.name}&quot; will permanently remove all
            associated data.
          </Typography>
        </Alert>

        <Typography variant="body1" gutterBottom>
          Before proceeding, please understand what will be deleted:
        </Typography>

        <List dense sx={{ mt: 2 }}>
          <ListItem>
            <ListItemIcon>
              <PeopleIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="All member data and relationships"
              secondary="Members will lose access to this studio"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <EventIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="All classes, schedules, and bookings"
              secondary="Past and future class data will be removed"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <PaymentIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Billing and subscription data"
              secondary="Payment history will be archived but inaccessible"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <StorageIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="All files, photos, and documents"
              secondary="Studio assets cannot be recovered"
            />
          </ListItem>
        </List>

        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowImpactDetails(!showImpactDetails)}
          sx={{ mt: 3 }}
          startIcon={<WarningIcon />}
        >
          {showImpactDetails ? 'Hide' : 'Show'} Detailed Impact Analysis
        </Button>

        {showImpactDetails && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              bgcolor: 'error.light',
              borderRadius: 2,
              border: 2,
              borderColor: 'error.main',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <WarningIcon color="error" />
              Current Studio Data That Will Be Lost
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Studio Information:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Chip
                    label={`${studio.memberCount || 0} members`}
                    size="medium"
                    color="error"
                  />
                  <Chip
                    label={studio.subscriptionStatus || 'No subscription'}
                    size="medium"
                    color={
                      studio.subscriptionStatus === 'active'
                        ? 'warning'
                        : 'default'
                    }
                  />
                  <Chip
                    label={
                      studio.isPersonal ? 'Personal Studio' : 'Team Studio'
                    }
                    size="medium"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Alert severity="error" variant="outlined">
                <Typography variant="body2">
                  <strong>All of this data will be permanently deleted</strong>{' '}
                  and cannot be recovered. This includes all historical records,
                  member relationships, class schedules, and uploaded files.
                </Typography>
              </Alert>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleProceedToConfirmation}
          color="error"
          variant="outlined"
          disabled={loading}
          startIcon={<WarningIcon />}
        >
          I Understand, Continue
        </Button>
      </DialogActions>
    </>
  );

  // Render confirmation step
  const renderConfirmationStep = () => (
    <>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1">
            To confirm deletion, please type the studio name exactly as shown
            below:
          </Typography>
        </Alert>

        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
            {studio.name}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Type studio name to confirm"
          value={nameVerification}
          onChange={e => setNameVerification(e.target.value)}
          error={nameVerification.length > 0 && !isNameVerified}
          helperText={
            nameVerification.length > 0 && !isNameVerified
              ? 'Studio name does not match'
              : 'This confirms you understand the consequences'
          }
          disabled={loading}
          autoFocus
        />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This verification step ensures you&apos;re certain about deleting this
          studio.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={() => setStep('warning')} disabled={loading}>
          Back
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleProceedToFinal}
          color="error"
          variant="outlined"
          disabled={loading || !isNameVerified}
          startIcon={<WarningIcon />}
        >
          Proceed to Final Step
        </Button>
      </DialogActions>
    </>
  );

  // Render final confirmation step
  const renderFinalStep = () => (
    <>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Final Warning
          </Typography>
          <Typography variant="body1">
            You are about to permanently delete &quot;{studio.name}&quot;.
          </Typography>
        </Alert>

        <Typography variant="body1" gutterBottom>
          This is your last chance to cancel. Once you click &quot;Delete
          Studio&quot;, the deletion process will begin immediately and cannot
          be stopped.
        </Typography>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.contrastText">
            <strong>Remember:</strong> All data will be permanently lost.
            Members will lose access to this studio immediately.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={() => setStep('confirmation')} disabled={loading}>
          Back
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleFinalDelete}
          color="error"
          variant="contained"
          disabled={loading}
          startIcon={<DeleteIcon />}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Deleting...' : 'Delete Studio'}
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {getDialogTitle()}
        </Box>
      </DialogTitle>

      <Divider />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Render current step */}
      {step === 'warning' && renderWarningStep()}
      {step === 'confirmation' && renderConfirmationStep()}
      {step === 'final' && renderFinalStep()}
    </Dialog>
  );
};

export default StudioDeleteDialog;
